import { gql, type TypedDocumentNode } from '@apollo/client';

import { CardPackCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';

import { BasicPacks } from '#src/components/commonCard/BuyPacks/BasicPacks/index.tsx';
import { PremiumPack } from '#src/components/commonCard/BuyPacks/PremiumPack/index.tsx';

import { CardPackGroupWrapper } from './CardPackGroupWrapper/index.tsx';
import { HighlightedPack } from './HighlightedPack/index.tsx';
import type { CardPackGroup_cardPackGroup } from './__generated__/index.graphql.ts';

const splitCardPacksByType = (
  cardPacks: CardPackGroup_cardPackGroup['cardPacks']
) => {
  const result = {
    basicPacks: [] as CardPackGroup_cardPackGroup['cardPacks'][number][],
    premiumPack: null as
      | CardPackGroup_cardPackGroup['cardPacks'][number]
      | null,
  };

  cardPacks.forEach(pack => {
    if (pack.currency === CardPackCurrency.COMMON_GEM) {
      result.premiumPack = pack;
    } else {
      result.basicPacks.push(pack);
    }
  });

  return result;
};

type Props = {
  cardPackGroup: CardPackGroup_cardPackGroup;
};
export const CardPackGroup = ({ cardPackGroup }: Props) => {
  const { cardPacks } = cardPackGroup;
  const { premiumPack, basicPacks } = splitCardPacksByType(cardPacks);

  const isLaptopAndAbove = useIsLaptop();
  return (
    <CardPackGroupWrapper>
      {isLaptopAndAbove ? (
        premiumPack && (
          <PremiumPack
            cardPack={premiumPack}
            cardPackGroupId={cardPackGroup.id}
          />
        )
      ) : (
        <HighlightedPack
          premiumCardPack={premiumPack}
          cardPackGroupId={cardPackGroup.id}
        />
      )}
      <BasicPacks cardPacks={basicPacks} cardPackGroupId={cardPackGroup.id} />
    </CardPackGroupWrapper>
  );
};

CardPackGroup.fragments = {
  cardPackGroup: gql`
    fragment CardPackGroup_cardPackGroup on CardPackGroup {
      id
      cardPacks {
        slug
        cardSet {
          slug
        }
        currency
        ...HighlightedPack_cardPack
        ...PremiumPack_cardPack
      }
    }
    ${PremiumPack.fragments.cardPack}
    ${HighlightedPack.fragments.cardPack}
  ` as TypedDocumentNode<CardPackGroup_cardPackGroup>,
};
